import { useTranslation } from 'react-i18next';
import { Badge, Gap, theme, Modal } from '@salecycle/ui-component-library';
import { useImportConsentContext } from '../context/ImportConsentContext';
import { RAGStatus, Amber, Red } from '../types';
import { SelectBoxArea, PreviewTableArea } from './AmberPreviewArea';
import { makeGreenStatus } from '../utils/ConsentFileStatusGenerator';

export function AnalysedFileModal() {
  const { t } = useTranslation('fileManagement');
  const {
    analysedFileData,
    analysedFileModalVisibility,
    dateHeadingSelected,
    emailHeadingSelected,
    fileData,
    setFileData,
    setAnalysedFileData,
    setAnalysedFileModalVisibility,
    setDateHeadingSelected,
    setEmailHeadingSelected,
  } = useImportConsentContext();

  // tell the compiler it's red but handle if it's not
  const errorReasonsInFile = (analysedFileData?.status as Red)?.errors ?? [];

  const handleClose = () => {
    setAnalysedFileModalVisibility(false);
  };

  const handleAmberModalDecline = () => {
    setDateHeadingSelected(null);
    setEmailHeadingSelected(null);
    setAnalysedFileModalVisibility(false);
  };

  const handleAmberModalAccept = () => {
    const wantedColumns = (analysedFileData.status as Amber).columns.filter(
      col => col.heading === emailHeadingSelected || col.heading === dateHeadingSelected,
    );

    const newFileData = {
      ...analysedFileData,
      status: makeGreenStatus(t, wantedColumns, analysedFileData.status.consentType),
    };

    const allNewFileData = fileData.map(consentData => {
      if (consentData.fileName !== analysedFileData.fileName) {
        return consentData;
      }

      return newFileData;
    });

    setAnalysedFileModalVisibility(false);
    setDateHeadingSelected(null);
    setEmailHeadingSelected(null);
    setAnalysedFileData(newFileData);
    setFileData(allNewFileData);
  };

  switch (analysedFileData?.status?.type) {
    case RAGStatus.RED: {
      return (
        <Modal
          isOpen={analysedFileModalVisibility}
          onCloseButtonClicked={handleClose}
          onOkButtonClicked={handleClose}
          heading={t('analysis.file.analysed.red.modal.title')}
          okButtonLabel={t('analysis.file.analysed.red.modal.confirm')}
          variation="clean"
          size="medium"
        >
          <Badge color={theme.vars.newColor.red2}>{analysedFileData.fileName}</Badge>
          <Gap bottom={20} />
          <p>{t('analysis.file.analysed.red.modal.text')}</p>
          <Gap bottom={20} />
          <ul style={{ paddingLeft: '40px' }}>
            {errorReasonsInFile.map((reason, index) => (
              <li key={`reason-${index}`}>{reason}</li>
            ))}
          </ul>
          <Gap bottom={20} />
          <p>{t('analysis.file.analysed.red.modal.textHowToResolve')}</p>
        </Modal>
      );
    }
    case RAGStatus.AMBER: {
      return (
        <Modal
          isOpen={analysedFileModalVisibility}
          onCloseButtonClicked={handleAmberModalDecline}
          onOkButtonClicked={handleAmberModalAccept}
          okButtonDisabled={!emailHeadingSelected}
          onCancelButtonClicked={handleAmberModalDecline}
          heading={t('analysis.file.analysed.amber.modal.title')}
          okButtonLabel={t('analysis.file.analysed.amber.modal.confirmButton')}
          variation="clean"
          size="medium"
        >
          <Badge color={theme.vars.newColor.orange2}>{analysedFileData.fileName}</Badge>
          <Gap bottom={20} />
          <p>{t('analysis.file.analysed.amber.modal.text')}</p>
          <Gap bottom={30} />
          <h4>
            <p>{t('analysis.file.analysed.amber.modal.fieldHeading')}</p>
          </h4>
          <Gap bottom={30} />
          <SelectBoxArea />
          <PreviewTableArea />
        </Modal>
      );
    }
    default: {
      return null;
    }
  }
}
